// core.ts — Helpers centraux du frontend.
//
// Migration S2.4 du plan TS+Vite (2026-05-10) : core.js (1145 LOC) → core.ts
// strict. Conversion mécanique avec types primitifs + interfaces minimales.
// Affinage des structures complexes (Risk, User, Action) prévu en S3+ avec
// le dossier `domain/`.
//
// Ce fichier reste source-of-truth runtime pour ~30 helpers exposés sur
// window. Les modules consommateurs continuent d'y accéder via window.X
// (bare-refs) tant qu'ils ne sont pas migrés en imports nommés (S4-S5).

// Règles métier pures de sévérité (libellé + snap couleur) : source de vérité
// dans domain/severity.ts, testée hors DOM. `sevColor`/`sevLabel` ci-dessous
// délèguent à ces fonctions (cf. domain/severity.ts).
import { severityLabel, nearestSeverityColor } from '../domain/severity.js';

// Routeur runtime (tranche extraite de ce fichier en août 2026, cf.
// router-runtime.ts). Réimporté ici uniquement pour le shim `window` ci-dessous,
// qui reste la porte d'entrée des pages (bare-refs `router.navigate(...)`).
import { router, registerPageDisposer, updateNav } from './router-runtime.js';

// Helpers PURS : `utils/dom.ts` et `utils/format.ts` sont la source de vérité.
// Écrits en mai 2026 pour l'être, ils sont restés testés mais importés par
// PERSONNE pendant que core.ts gardait ses copies non testées — dont un
// `todayStr` en UTC, alors que la correction en date locale existait et
// passait ses tests (audit front du 2026-09-10).
// Verrouillé par `tests/test_front_module_hygiene.py`.
import { esc } from '../utils/dom.js';
import { armHeaderButtons } from '../utils/header-buttons.js';
import { fmtDate, priorityLabel, riskCodeOrFallback, todayStr } from '../utils/format.js';

// ── Types & globals déclarés ──────────────────────────────────────────────

// Interfaces minimales locales (à enrichir / remplacer par domain/ en S3+).
interface ActionStatusLite {
  id: number;
  position: number;
}
interface UserLite {
  id: number;
  trigram?: string | null;
  name: string;
  is_team?: boolean;
  is_active?: boolean;
  /** YYYY-MM-DD ; passé cette date l'utilisateur est exclu des pickers. */
  departure_date?: string | null;
}
interface StageLite {
  id: number;
}
interface RiskListLite {
  id: number;
}
interface RiskColorRow {
  severity: number;
  color: string;
}
interface CommitteeLite {
  id: number;
  code: string;
  name: string;
}
interface I18nCatalog {
  [key: string]: string;
}

// Cache mémoire centralisé. Source de vérité pour les listes référentielles
// chargées une fois par session (puis invalidées explicitement post-CRUD).
interface CoreCache {
  users: UserLite[] | null;
  stages: StageLite[] | null;
  riskLists: RiskListLite[] | null;
  actionStatuses: ActionStatusLite[] | null;
  committees: CommitteeLite[] | null;
}

// Spec d'un champ pour openFormModal.
interface FormField {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'email' | 'select' | 'textarea' | 'checkbox';
  value?: string | number | boolean | null;
  required?: boolean;
  options?: ReadonlyArray<{ value: string | number; label: string }>;
}

// Pages projet state : défini par page-project, consommé ici pour
// getDefaultStatusId / commitNewActions. L'interface est déclarée
// globalement dans core-globals.d.ts (déclaration mergeable) ;
// page-project/index.ts ajoute les champs métier complets.

// Pages — handlers de routes enregistrés par les modules de pages.
//
// La signature reste `Record<string, string>` au runtime (rétrocompat avec
// `pages.adminXxx?.({})` utilisé pour re-render post-CRUD). Les handlers
// typés (cf. router.ts > definePage) sont casts vers PageHandler à
// l'enregistrement — le helper applique la coercion `string → number` avant
// d'appeler le vrai handler typé.
type PageHandler = (params: Record<string, string>) => void | Promise<void>;
type PagesGlobal = Record<string, PageHandler> & { dashboard?: PageHandler };

// Chart.js (chargé via CDN UMD avant le bundle Vite).
type ChartConstructor = new (canvas: HTMLCanvasElement, config: unknown) => { destroy(): void };

declare global {
  interface Window {
    __i18n?: I18nCatalog;
    __i18nLocale?: string;
    __riskColors?: Record<number, string>;
    _cachedCommittees?: CommitteeLite[];
    pages?: PagesGlobal;
    ps?: ProjectStateLike;
    Chart?: ChartConstructor;
  }
  // Chart.js global UMD (utilisé via `new Chart(...)` sans import).
  // Déclaré en `var` pour matcher le runtime (Chart.js attache window.Chart).

  var Chart: ChartConstructor | undefined;
  // ps : state de page-project, exposé en bare-ref par window.ps.

  var ps: ProjectStateLike;
  // pages : registry des handlers de routes, exposé par les modules pages.

  var pages: PagesGlobal;
  // api : déclaré dans api.ts, accessible aussi en bare-ref via window.api.

  var api: Window['api'];
}

// ── Globals internes au module ─────────────────────────────────────────────

// Init runtime du registry des pages. Avant la migration ESM/TS, c'était
// `const pages = {};` au top de page-projects.js. La déclaration de TYPE
// vit dans `declare global { var pages: PagesGlobal }` ci-dessus ; ici on
// pose la valeur pour que tous les modules `pages.X = ...` aient un objet
// cible. Le set sur window est volontaire (compat JS scripts éventuels).
if (typeof (globalThis as { pages?: PagesGlobal }).pages === 'undefined') {
  (globalThis as { pages?: PagesGlobal }).pages = {} as PagesGlobal;
}

const _cache: CoreCache = {
  users: null,
  stages: null,
  riskLists: null,
  actionStatuses: null,
  committees: null,
};
let _currentUserId: number | null =
  parseInt(sessionStorage.getItem('currentUserId') || '0') || null;

/**
 * ID de l'utilisateur actuellement authentifié (lecture synchrone).
 *
 * Source de vérité : `window.__authUser` (posé par `auth_ui.js` après
 * `GET /api/v1/auth/me`). La variable `_currentUserId` est un cache
 * historique.
 *
 * Le dropdown "Sélectionner un utilisateur" a été retiré : l'identité
 * vient exclusivement du cookie de session.
 */
function currentUserId(): number | null {
  const fromAuth = (window.__authUser && window.__authUser.id) || null;
  if (fromAuth) {
    if (_currentUserId !== fromAuth) {
      _currentUserId = fromAuth;
      // sessionStorage.setItem peut throw en mode privé Safari/iOS quand
      // le quota par origine est saturé. On garde la valeur en mémoire
      // (_currentUserId) et on continue sans bloquer l'utilisateur.
      try {
        sessionStorage.setItem('currentUserId', String(fromAuth));
      } catch (e) {
        safeWarn('sessionStorage.setItem currentUserId (sync)', e);
      }
    }
    return fromAuth;
  }
  return _currentUserId || null;
}

/**
 * Variante async qui **garantit** un userId en faisant un `/auth/me`
 * à la volée si nécessaire. À utiliser au début des pages qui ont besoin
 * de l'user avant le premier render.
 *
 * Résout la race init : `auth_ui.js` charge `__authUser` en parallèle
 * du router, donc la première navigation peut arriver avant que
 * `__authUser` soit posé. Plutôt que de bloquer avec un loader muet,
 * on force la résolution ici.
 */
async function ensureCurrentUserId(): Promise<number | null> {
  const uid = currentUserId();
  if (uid) return uid;
  try {
    const u = (await api.me()) as {
      id: number;
      trigram: string;
      name: string;
      role: string;
    } | null;
    if (u && u.id) {
      window.__authUser = u;
      _currentUserId = u.id;
      // Cf. note dans currentUserId() — Safari mode privé peut throw.
      try {
        sessionStorage.setItem('currentUserId', String(u.id));
      } catch (e) {
        safeWarn('sessionStorage.setItem currentUserId (async)', e);
      }
      return u.id;
    }
  } catch (e: unknown) {
    // 401 attendu si cookie expiré/absent → auth_ui.ts affichera l'overlay.
    // Toute autre erreur (réseau, 5xx) mérite un log pour diagnostic.
    const err = e as { status?: number } | null;
    if (err?.status !== 401) safeWarn('ensureCurrentUserId', e);
  }
  return null;
}

// ── Utils ─────────────────────────────────────────────────────────────────

// ── Globals partagés ─────────────────────────────────────────────────────
// Convention : les globals exposés sur `window` sont préfixés par `__`
// pour indiquer qu'ils sont internes à l'app (style « ne pas toucher
// depuis du code tiers »). Liste exhaustive :
//
// - `window.__authUser`    : utilisateur courant. Posé par auth_ui.js
//                            après /auth/me. Lu par app.js et permissions_ui.js.
// - `window.__riskColors`  : map { severity: hex } des couleurs de risque.
//                            Cache mémoire, source = table risk_colors.
//
// Les variables `window._cachedCommittees`, `window._selectedOriginId`,
// `window._actionModalUsers` sont des relicats d'un design plus ancien
// où l'état UI de la modale d'action était partagé via window. À
// encapsuler dans une closure dédiée lors d'un futur refacto. Pour
// l'instant, on les laisse en place : changer leur portée demande
// de toucher tous les sites qui les lisent (5+ chacun).

// ── Convention sur l'égalité ─────────────────────────────────────────────
// On utilise `===` strict partout, SAUF pour `x == null` qui est le
// pattern idiomatique pour matcher null OU undefined en une seule
// expression. Ne PAS remplacer `== null` par `=== null` : ça raterait
// les undefined et casserait silencieusement les checks de fallback.

// ── Convention sur les event listeners ────────────────────────────────────
// Le ratio addEventListener / removeEventListener est intentionnellement
// déséquilibré (~195 / 5). Ce n'est PAS un leak. Règles :
// 1. Listeners attachés à des éléments DOM éphémères (lignes de table,
//    contenu de modale, items de liste) : pas besoin de remove. Le GC
//    les libère quand le parent est remplacé par `innerHTML = ...`.
// 2. Listeners attachés à `document` ou `window` qui doivent vivre
//    plus longtemps que le contexte courant : DOIVENT avoir une paire
//    explicite `removeEventListener`. Cf. patterns origin picker
//    (L4685+) et account menu (L8147+).
// 3. La délégation d'événement (`e.target.closest()`) est privilégiée
//    pour les listes/tables qui se re-rendent — on attache UN listener
//    au parent stable et on dispatche par sélecteur enfant. Le
//    listener parent est posé une fois et reste valide tant que le
//    parent existe.

// ── Helpers de robustesse ─────────────────────────────────────────────────
// Centralisent la gestion des erreurs « non bloquantes » qui étaient
// avalées par des `catch (_) {}` disséminés. Deux objectifs :
// 1. Garder un signal côté console pour diagnostiquer les bugs en prod
//    sans bombarder l'utilisateur de toasts.
// 2. Standardiser le pattern d'appel défensif à `toast()` (qui peut
//    n'être pas encore chargé au tout début du boot).
//
// Cibles navigateurs : Chrome / Safari / Edge versions 2025+.
// `console.warn` est universellement supporté ; pas de polyfill.

function safeWarn(context: string, error: unknown): void {
  // Logger non bloquant pour les erreurs avec fallback en place.
  // Ne JAMAIS appeler depuis un endroit où l'erreur impacte l'UX :
  // dans ce cas, utiliser `safeToast(...)` ou laisser remonter.
  const msg = error instanceof Error ? error.message : error;
  console.warn(`[pmo] ${context}`, msg);
}

// ── localStorage tenant-isolated wrappers (audit P0 #5) ─────────────────────
// Préfixe les clés localStorage par hostname pour isoler les tenants
// — chaque tenant a son sous-domaine en prod (et un hostname distinct
// via lvh.me / localtest.me en dev). Sans préfixe, un user qui bascule
// d'un tenant à l'autre voyait persister ses filtres / state UI à
// travers les tenants (latent : pas exploité aujourd'hui car 1 user =
// 1 tenant en pratique, mais à durcir avant multi-comptes admin).
//
// safeWrap autour de localStorage : Safari mode privé peut throw
// QuotaExceededError au setItem. On log et on continue silencieusement.
function _tlsKey(key: string): string {
  return `${window.location.hostname}:${key}`;
}
function tlsSet(key: string, val: string): void {
  try {
    localStorage.setItem(_tlsKey(key), val);
  } catch (e) {
    safeWarn(`tlsSet(${key})`, e);
  }
}
function tlsGet(key: string): string | null {
  try {
    return localStorage.getItem(_tlsKey(key));
  } catch (e) {
    safeWarn(`tlsGet(${key})`, e);
    return null;
  }
}
function tlsRemove(key: string): void {
  try {
    localStorage.removeItem(_tlsKey(key));
  } catch (e) {
    safeWarn(`tlsRemove(${key})`, e);
  }
}

type ToastLevel = 'ok' | 'info' | 'success' | 'warning' | 'error';

function safeToast(message: string, level: ToastLevel = 'info'): void {
  // Safe wrapper autour de `toast()`. Intentionnellement défensif :
  // - `toast` peut n'être pas encore défini au tout début du boot
  //   (avant que le DOM principal soit injecté).
  // - Si l'appel à `toast` plante, on log et on continue sans casser
  //   le flux courant (le caller veut juste informer, pas bloquer).
  try {
    if (typeof toast === 'function') toast(message, level);
  } catch (e) {
    safeWarn('safeToast failed', e);
  }
}

// ── i18n ────────────────────────────────────────────────────────────────────
// Source de vérité des libellés UI : catalog statique
// (/static/i18n/<locale>.json) + overrides par tenant (table
// i18n_overrides) mergés côté backend par GET /api/v1/i18n?locale=<l>.
//
// Boot : boot.js appelle `await loadI18n()` AVANT `router.init()` pour
// garantir que `t(...)` répond correctement dès le premier render.
// En cas d'échec (réseau, JSON invalide), `window.__i18n` reste `{}` et
// `t(key)` renverra la clé brute — visible mais non bloquant.
//
// Cache HTTP : le serveur pose un ETag composite
// (version_image + max(updated_at) overrides), le navigateur fait
// If-None-Match → 304. Pas de cache busting URL nécessaire.

const I18N_DEFAULT_LOCALE = 'fr';
const I18N_INTERP_RE = /\{(\w+)\}/g;

async function loadI18n(locale: string = I18N_DEFAULT_LOCALE): Promise<void> {
  // api.ts est chargé avant core.ts dans main.ts — on peut s'en servir
  // ici. En cas de 401, api.req déclenche auth:expired (même gestion
  // que /auth/me). Le navigateur honore l'ETag automatiquement sans
  // option supplémentaire.
  try {
    const data = (await api.getI18n(locale)) as I18nCatalog;
    window.__i18n = data;
    window.__i18nLocale = locale;
  } catch (e) {
    safeWarn(`loadI18n(${locale})`, e);
    window.__i18n = window.__i18n || {};
  }
}

// Patch des éléments HTML statiques via attributs data-i18n* — utile pour
// les libellés figés dans index.html (section-labels du sidebar, titres
// brand, etc.) qui sont rendus AVANT que loadI18n() ait peuplé
// window.__i18n.
//
// Conventions :
//   <span data-i18n="key">Fallback FR</span>
//     → setAttribute textContent
//   <button data-i18n-title="key" data-i18n-aria-label="key" title="..." aria-label="...">
//     → setAttribute(title), setAttribute(aria-label)
//
// Le contenu/attribut FR statique en index.html sert de fallback si
// l'i18n échoue à charger (offline, 401, etc.) — l'app reste lisible.
function applyI18nDom(): void {
  const cat = window.__i18n;
  if (!cat) return;
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const k = el.dataset['i18n'];
    if (!k) return;
    const v = cat[k];
    if (v != null) el.textContent = v;
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    const k = el.dataset['i18nTitle'];
    if (!k) return;
    const v = cat[k];
    if (v != null) el.setAttribute('title', v);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-aria-label]').forEach((el) => {
    const k = el.dataset['i18nAriaLabel'];
    if (!k) return;
    const v = cat[k];
    if (v != null) el.setAttribute('aria-label', v);
  });
}

function t(key: string, params?: Record<string, unknown> | null): string {
  // Si i18n pas encore chargé (race au boot), on renvoie la clé brute :
  // c'est visible côté UI (utile en dev), pas crashant en prod.
  const cat = window.__i18n;
  if (!cat || !(key in cat)) {
    if (cat) safeWarn('t', new Error(`Clé i18n manquante: ${key}`));
    return key;
  }
  const raw = cat[key] ?? key;
  if (!params) return raw;
  // Auto-échappement des valeurs interpolées : t() est destiné à être
  // injecté dans innerHTML, donc tout `{param}` qui contient du
  // user-content doit être HTML-safe par défaut. Si un caller veut
  // injecter du HTML brut intentionnellement, il doit le concaténer
  // hors du template t() (ex. `${t('foo')} <strong>${trustedHtml}</strong>`).
  // Les mots-clés du template (HTML structurel comme <strong>) ne sont
  // pas concernés — seules les valeurs des `params` passent par esc().
  return raw.replace(I18N_INTERP_RE, (m: string, p: string) =>
    params[p] != null ? esc(params[p]) : m,
  );
}

// ── Couleurs de sévérité ────────────────────────────────────────────────────
// Source de vérité : table `risk_colors` (page #admin/risk-colors). Chargée
// au boot via `loadRiskColors()`, stockée dans `window.__riskColors` sous
// forme { [severity:number]: "#RRGGBB" }. `sevColor(n)` retourne le hex
// configuré, avec un fallback gris si la sévérité n'est pas connue.

const FALLBACK_SEV_COLOR = '#cccccc';

async function loadRiskColors(force = false): Promise<Record<number, string>> {
  if (!force && window.__riskColors) return window.__riskColors;
  try {
    const rows = (await api.getRiskColors()) as RiskColorRow[];
    const map: Record<number, string> = {};
    for (const r of rows) map[r.severity] = r.color;
    window.__riskColors = map;
    return map;
  } catch (e) {
    // Si l'API tombe, on renvoie les couleurs déjà cachées (ou {} en
    // tout début de session). `sevColor()` fera le fallback gris pour
    // les sévérités non mappées. On log pour diagnostic.
    safeWarn('loadRiskColors', e);
    window.__riskColors = window.__riskColors || {};
    return window.__riskColors;
  }
}

function sevColor(s: number): string {
  // Snap vers la sévérité DISPONIBLE la plus proche dans la table (évite les
  // trous gris sur les valeurs non-listées : 5, 7, 10, 11, 13-15). Logique pure
  // déléguée à domain/severity.ts ; ici on n'injecte que la palette runtime.
  return nearestSeverityColor(window.__riskColors || {}, s, FALLBACK_SEV_COLOR);
}

const sevLabel = severityLabel;

function scaleBtnHtml(riskId: number, type: string, n: number, activeVal: number): string {
  const active = n === activeVal ? ` active-${n}` : '';
  const problem = '';
  const impactCls = type === 'impact' ? ' impact-btn' : '';
  const title = '';
  return `<button class="scale-btn${active}${problem}${impactCls}" data-scale-risk="${riskId}" data-scale-type="${type}" data-scale-val="${n}" ${title}>${n}</button>`;
}

function scaleHtml(riskId: number, type: string, value: number): string {
  const max = 4; // both prob and impact go 1-4
  let h = '<div class="scale">';
  for (let n = 1; n <= max; n++) h += scaleBtnHtml(riskId, type, n, value);
  h += '</div>';
  return h;
}

function getDefaultStatusId(): number | null {
  const psState = (typeof ps !== 'undefined' ? ps : null) as ProjectStateLike | null;
  const statuses = psState?.actionStatuses || _cache.actionStatuses || [];
  const sorted = [...statuses].sort((a, b) => a.position - b.position);
  return sorted.length > 0 ? (sorted[0]?.id ?? null) : null;
}

function commitNewActions(riskId: number): void {
  // Freeze all _isNew actions for a risk into display mode
  const psState = (typeof ps !== 'undefined' ? ps : null) as ProjectStateLike | null;
  const entries = psState?.entries;
  if (!entries) return;
  const e = entries[riskId];
  if (!e) return;
  for (const a of e.actions || []) {
    if (a._isNew) a._isNew = false;
  }
}

// Convertit un *datetime* ISO (UTC, le backend ne suffixe pas toujours 'Z')
// en date locale DD/MM/YYYY. À utiliser pour les colonnes "créé le / modifié
// le" au lieu de tronquer l'ISO à la date UTC via `.split('T')[0]`, qui
// décale d'un jour aux bords de journée selon le fuseau de l'utilisateur.
// Cf. règle projet : "datetimes UTC backend → heure locale à l'affichage".
function fmtDateLocal(s: string | null | undefined): string {
  if (!s) return '—';
  const str = String(s);
  // Date pure (sans composante horaire) : aucune conversion de fuseau requise.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return fmtDate(str);
  // Datetime : force l'interprétation UTC si le suffixe Z/offset est absent.
  const iso = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str) ? str : str + 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fmtDate(str);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

// Variante datetime de fmtDateLocal : DD/MM/YYYY HH:MM en heure locale.
// Pour les tooltips / horodatages où l'heure compte (notes, journal).
function fmtDateTimeLocal(s: string | null | undefined): string {
  if (!s) return '';
  const str = String(s);
  const iso = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str) ? str : str + 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format d'affichage standard d'un user — utilisé par tous les modules
// qui affichent un utilisateur (RACI, pickers, sélecteurs). `short=true`
// → trigram seul (ou 3 premiers car de name si pas de trigram), sinon
// "TRI — Nom complet". Null-safe : retourne '?' si u est null.
//
// Vivait avant dans page-project.js mais utilisé par 3 autres modules
// (audit P1 #6 mai 2026) — cassait le contrat d'indépendance inter-modules
// documenté dans index.html. Centralisé ici en compagnie des autres
// helpers d'affichage (sevColor, fmtDate, priorityLabel, …).
function userLabel(u: UserLite | null | undefined, short?: boolean): string {
  if (!u) return '?';
  const tri = u.trigram ? u.trigram.toUpperCase() : null;
  if (short) return tri || u.name.slice(0, 3).toUpperCase();
  return tri ? `${tri} — ${u.name}` : u.name;
}

// Only individual users (not teams) can be responsible
function individualUserOptions(
  users: ReadonlyArray<UserLite>,
  selectedId: number | null | undefined,
): string {
  let h = '<option value="">—</option>';
  for (const u of users) {
    if (u.is_team) continue;
    h += `<option value="${u.id}" ${u.id === selectedId ? 'selected' : ''}>${esc(userLabel(u, false))}</option>`;
  }
  return h;
}

// ── Toast ─────────────────────────────────────────────────────────────────

function toast(msg: string, type: ToastLevel = 'ok'): void {
  const c = document.getElementById('toast-container');
  if (!c) return;
  const el = document.createElement('div');
  el.className = 'toast toast-' + type;
  el.textContent = msg;
  // a11y (audit P2 #14) : le conteneur a aria-live="polite" + role="status"
  // pour les toasts succès/info. Pour les erreurs, on pose role="alert"
  // localement sur l'élément — annoncé immédiatement par les screen
  // readers (interrompt si nécessaire), au lieu d'attendre le silence
  // comme polite.
  if (type === 'error') el.setAttribute('role', 'alert');
  c.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

// ── Modal ─────────────────────────────────────────────────────────────────

// Mémoire du focus pour le restaurer après fermeture (a11y).
let _modalPrevFocus: Element | null = null;

type ModalConfirm = () => void | Promise<void>;

// a11y : piège le focus à l'intérieur de l'overlay. Tant que la modale est
// ouverte, Tab / Shift+Tab bouclent sur les éléments focusables du dialogue
// au lieu de "fuir" vers le contenu de fond (incohérent avec aria-modal).
// Le listener est attaché à l'overlay lui-même : il disparaît avec lui au
// closeModal() (l'overlay est retiré du DOM), pas besoin de nettoyage manuel.
function _installFocusTrap(overlay: HTMLElement): void {
  overlay.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      overlay.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !overlay.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !overlay.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}

function openModal(
  title: string,
  bodyHtml: string,
  onConfirm: ModalConfirm,
  confirmLabel?: string | null,
): void {
  // Les défauts i18n sont résolus à l'appel (pas à la définition) pour
  // que loadI18n() ait eu le temps de remplir window.__i18n au boot.
  const cl = confirmLabel != null ? confirmLabel : t('common.confirm');
  closeModal();
  _modalPrevFocus = document.activeElement;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'modal-title');
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2 id="modal-title">${esc(title)}</h2>
        <button class="btn btn-secondary btn-sm" id="modal-close" aria-label="${esc(t('common.close'))}">✕</button>
      </div>
      <div class="modal-body" id="modal-body">${bodyHtml}</div>
      <div class="modal-footer">
        <button class="btn btn-neutral" id="modal-cancel">${esc(t('common.cancel'))}</button>
        <button class="btn btn-primary" id="modal-confirm">${esc(cl)}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  _normalizeIconButtonAria(overlay);
  _installFocusTrap(overlay);
  // Snapshot des champs pour détecter les modifications non sauvegardées.
  setTimeout(() => _snapshotModalFields(overlay), 0);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) void closeModalSafe();
  });
  document.getElementById('modal-close')?.addEventListener('click', () => void closeModalSafe());
  document.getElementById('modal-cancel')?.addEventListener('click', () => void closeModalSafe());
  // UX : on désactive le bouton confirm pendant l'exécution de onConfirm
  // pour empêcher les double-clics qui créeraient des doublons backend.
  // Si onConfirm est synchrone (toast d'erreur immédiat), le bouton n'est
  // disabled qu'un tick — invisible mais sans effet de bord.
  const confirmBtn = document.getElementById('modal-confirm') as HTMLButtonElement | null;
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      void api.withButton(confirmBtn, async () => onConfirm());
    });
  }
  // a11y : focus initial sur le premier input éditable du body, sinon sur
  // le bouton confirmer. Esc ferme la modale.
  setTimeout(() => {
    const firstInput = overlay.querySelector<HTMLElement>(
      '.modal-body input:not([type=hidden]), .modal-body select, .modal-body textarea',
    );
    (firstInput || confirmBtn)?.focus();
  }, 0);
}

// `hideCancel` : pour les modales "info-only" (ex. À propos / changelog)
// où "Annuler" + "Fermer" est redondant. Le bouton primaire fait alors office
// de seule sortie utilisateur (en plus de la croix et du clic extérieur).
function openWideModal(
  title: string,
  bodyHtml: string,
  onConfirm: ModalConfirm,
  confirmLabel?: string | null,
  hideCancel = false,
): void {
  const cl = confirmLabel != null ? confirmLabel : t('common.save');
  closeModal();
  _modalPrevFocus = document.activeElement;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'modal-title');
  overlay.style.cssText = 'align-items:flex-start;padding:16px';
  const cancelBtnHtml = hideCancel
    ? ''
    : `<button class="btn btn-neutral" id="modal-cancel">${esc(t('common.cancel'))}</button>`;
  overlay.innerHTML = `
    <div class="modal" style="max-width:min(1100px,98vw);width:100%;max-height:calc(100vh - 32px);display:flex;flex-direction:column;margin-top:0">
      <div class="modal-header" style="flex-shrink:0">
        <h2 id="modal-title">${esc(title)}</h2>
        <button class="btn btn-secondary btn-sm" id="modal-close" aria-label="${esc(t('common.close'))}">✕</button>
      </div>
      <div class="modal-body" id="modal-body" style="overflow-y:auto;flex:1">${bodyHtml}</div>
      <div class="modal-footer" style="flex-shrink:0">
        ${cancelBtnHtml}
        <button class="btn btn-primary" id="modal-confirm">${esc(cl)}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  _normalizeIconButtonAria(overlay);
  _installFocusTrap(overlay);
  setTimeout(() => _snapshotModalFields(overlay), 0);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) void closeModalSafe();
  });
  document.getElementById('modal-close')?.addEventListener('click', () => void closeModalSafe());
  document.getElementById('modal-cancel')?.addEventListener('click', () => void closeModalSafe());
  const confirmBtn = document.getElementById('modal-confirm') as HTMLButtonElement | null;
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      void api.withButton(confirmBtn, async () => onConfirm());
    });
  }
  setTimeout(() => {
    const firstInput = overlay.querySelector<HTMLElement>(
      '.modal-body input:not([type=hidden]), .modal-body select, .modal-body textarea',
    );
    (firstInput || confirmBtn)?.focus();
  }, 0);
}

// closeModal() : ferme inconditionnellement. Appelé par les modules après
// un Enregistrer/Supprimer réussi. Conserve la rétro-compatibilité avec
// tous les callsites existants.
function closeModal(): void {
  const el = document.getElementById('modal-overlay');
  if (!el) return;
  el.remove();
  // a11y : restaure le focus sur l'élément qui avait le focus avant l'ouverture.
  if (
    _modalPrevFocus &&
    'focus' in _modalPrevFocus &&
    typeof (_modalPrevFocus as HTMLElement).focus === 'function'
  ) {
    try {
      (_modalPrevFocus as HTMLElement).focus();
    } catch {
      /* élément retiré entre-temps */
    }
    _modalPrevFocus = null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers de modales standardisées (lot DT5)
// ═══════════════════════════════════════════════════════════════════════════
// Réduisent la duplication des show*Modal en 2 patterns récurrents :
//   - confirmation destructive (suppression / désactivation)
//   - formulaire simple (champs + boutons)
// Tous deux utilisent openModal en interne, donc bénéficient automatiquement
// de l'a11y (role=dialog, focus initial, Esc, focus restoration), du loading
// state du bouton confirm, et du prompt anti-perte de modifications.

/**
 * Modale de confirmation destructive avec bouton rouge.
 * Le `closeModal()` est implicite après que `onConfirm` se résout sans throw.
 *
 * Usage :
 *   openConfirmDangerModal(
 *     'Supprimer le projet',
 *     `Supprimer <strong>${esc(name)}</strong> ? Action irréversible.`,
 *     async () => {
 *       await api.deleteProject(id);
 *       toast('Projet supprimé');
 *       pages.dashboard();
 *     }
 *   );
 *
 * @param {string} title       Titre de la modale (texte brut).
 * @param {string} messageHtml Corps HTML déjà escapé par le caller.
 * @param {Function} onConfirm Async callback. Throw pour empêcher la fermeture.
 * @param {object}  opts       Optionnel : { confirmLabel: 'Supprimer' }.
 */
function openConfirmDangerModal(
  title: string,
  messageHtml: string,
  onConfirm: ModalConfirm,
  opts: { confirmLabel?: string } = {},
): void {
  const label = opts.confirmLabel || t('common.delete');
  openModal(
    title,
    `<p style="margin:0">${messageHtml}</p>`,
    async () => {
      try {
        await onConfirm();
        closeModal();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    label,
  );
  // Bascule le bouton confirm en btn-danger une fois le DOM injecté.
  setTimeout(() => {
    const btn = document.getElementById('modal-confirm');
    if (btn) {
      btn.classList.remove('btn-primary');
      btn.classList.add('btn-danger');
    }
  }, 0);
}

// confirmStackModal : confirm() bloquant remplaçable depuis l'intérieur
// d'une modale ouverte. Crée un overlay supplémentaire au-dessus de
// `#modal-overlay` (z-index `--z-modal-stack`) sans toucher à la modale
// parent. Promise<bool> — résolue à true sur OK / Enter, false sur Cancel
// / Escape / clic fond. Évite le `window.confirm()` natif (P2 #13).
//
// Usage :
//   const ok = await confirmStackModal({ message: 'Continuer ?', danger: true });
//   if (!ok) return;
interface ConfirmStackOpts {
  message?: string;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

function confirmStackModal({
  message = '',
  danger = false,
  confirmLabel,
  cancelLabel: cancelLabelOpt,
}: ConfirmStackOpts = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay modal-stack';
    const okLabel = esc(confirmLabel || t('common.confirm'));
    const cancelLabel = esc(cancelLabelOpt || t('common.cancel'));
    const okClass = danger ? 'btn-danger' : 'btn-primary';
    overlay.innerHTML = `
      <div class="modal modal-confirm-stack">
        <div class="modal-body" style="margin:0 0 16px">${esc(message)}</div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-stack-act="cancel">${cancelLabel}</button>
          <button class="btn ${okClass}" data-stack-act="ok">${okLabel}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const cleanup = (val: boolean): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey, true);
      resolve(val);
    };
    overlay.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null;
      const act = target?.closest<HTMLElement>('[data-stack-act]')?.dataset['stackAct'];
      if (act === 'ok') cleanup(true);
      else if (act === 'cancel' || e.target === overlay) cleanup(false);
    });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cleanup(false);
      } else if (e.key === 'Enter') {
        e.stopPropagation();
        cleanup(true);
      }
    };
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => overlay.querySelector<HTMLElement>('[data-stack-act="ok"]')?.focus(), 0);
  });
}

/**
 * Modale formulaire avec champs déclarés en data, valeurs collectées en dict.
 * Le `closeModal()` est implicite après que `onSubmit(values)` se résout
 * sans throw.
 *
 * Champs supportés : text, number, select, textarea, checkbox.
 *
 * Usage :
 *   openFormModal('Nouvelle étape', [
 *     { name: 'name',     label: 'Nom',      type: 'text',   required: true, value: '' },
 *     { name: 'position', label: 'Position', type: 'number', value: 0 },
 *   ], async (values) => {
 *     await api.createStage(values);
 *     toast('Étape créée');
 *     pages.adminStages();
 *   }, { title: 'Nouvelle étape', confirmLabel: 'Ajouter' });
 *
 * @param {Array<object>} fields  Spec des champs (name/label/type/value/options/required).
 * @param {Function}      onSubmit Async (values) => void. Throw pour bloquer la fermeture.
 * @param {object}        opts    { title, confirmLabel }.
 */
type FormValues = Record<string, string | number | boolean | null>;
type FormSubmit = (values: FormValues) => void | Promise<void>;

function openFormModal(
  fields: ReadonlyArray<FormField>,
  onSubmit: FormSubmit,
  opts: { title?: string; confirmLabel?: string } = {},
): void {
  const title = opts.title || t('common.formTitle');
  const confirmLabel = opts.confirmLabel || t('common.save');
  const body = fields
    .map((f) => {
      const id = `fm-${f.name}`;
      const required = f.required ? 'required' : '';
      if (f.type === 'select') {
        const options = (f.options || [])
          .map(
            (o) =>
              `<option value="${esc(String(o.value))}" ${String(o.value) === String(f.value || '') ? 'selected' : ''}>${esc(o.label)}</option>`,
          )
          .join('');
        return `<div class="form-group"><label for="${id}">${esc(f.label)}</label>
        <select id="${id}" class="w-full" ${required}>${options}</select></div>`;
      }
      if (f.type === 'textarea') {
        return `<div class="form-group"><label for="${id}">${esc(f.label)}</label>
        <textarea id="${id}" class="w-full" ${required}>${esc(f.value || '')}</textarea></div>`;
      }
      if (f.type === 'checkbox') {
        return `<div class="form-group"><label for="${id}" class="flex items-center gap-2">
        <input type="checkbox" id="${id}" ${f.value ? 'checked' : ''}>
        <span>${esc(f.label)}</span></label></div>`;
      }
      // text, number, date, email, etc.
      const type = f.type || 'text';
      return `<div class="form-group"><label for="${id}">${esc(f.label)}</label>
      <input type="${type}" id="${id}" class="w-full" value="${esc(String(f.value ?? ''))}" ${required}></div>`;
    })
    .join('');

  openModal(
    title,
    body,
    async () => {
      const values: FormValues = {};
      for (const f of fields) {
        const el = document.getElementById(`fm-${f.name}`) as
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
        if (!el) continue;
        if (el instanceof HTMLInputElement && el.type === 'checkbox') {
          values[f.name] = el.checked;
        } else if (f.type === 'number') {
          values[f.name] = el.value === '' ? null : Number(el.value);
        } else {
          values[f.name] = el.value;
        }
      }
      try {
        await onSubmit(values);
        closeModal();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    confirmLabel,
  );
}

// closeModalSafe() : ferme avec garde anti-perte de modifications. Utilisé
// par les chemins "Annuler" (bouton, ✕, overlay, Esc). Si le formulaire
// a été modifié depuis l'ouverture, prompt avant de fermer.
async function closeModalSafe(): Promise<void> {
  const el = document.getElementById('modal-overlay');
  if (!el) return;
  if (_modalHasUnsavedChanges(el)) {
    const ok = await confirmStackModal({
      message: t('common.unsaved_changes_confirm'),
    });
    if (!ok) return;
  }
  closeModal();
}

// Inputs supportés par le snapshot des modales (uniquement ceux qui ont
// .value et/ou .checked).
type ModalField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

// Détecte si la modale a des champs modifiés vs leur état initial.
// On compare la valeur courante des inputs/selects/textarea à la valeur
// snapshot prise à l'ouverture (cf. _snapshotModalFields). Pas parfait
// (n'attrape pas les modifs dans des éditeurs custom de tag-pickers), mais
// couvre 90% des cas formulaires standards.
function _modalHasUnsavedChanges(overlay: HTMLElement): boolean {
  const fields = overlay.querySelectorAll<ModalField>(
    '.modal-body input, .modal-body select, .modal-body textarea',
  );
  for (const f of fields) {
    const initial = f.dataset['modalInit'];
    if (initial === undefined) continue;
    const isCheckable =
      f instanceof HTMLInputElement && (f.type === 'checkbox' || f.type === 'radio');
    const current = isCheckable ? String((f as HTMLInputElement).checked) : f.value || '';
    if (current !== initial) return true;
  }
  return false;
}

function _snapshotModalFields(overlay: HTMLElement): void {
  overlay
    .querySelectorAll<ModalField>('.modal-body input, .modal-body select, .modal-body textarea')
    .forEach((f) => {
      const isCheckable =
        f instanceof HTMLInputElement && (f.type === 'checkbox' || f.type === 'radio');
      f.dataset['modalInit'] = isCheckable
        ? String((f as HTMLInputElement).checked)
        : f.value || '';
    });
}

// a11y : Esc ferme toute modale ouverte (avec garde anti-perte de modifs).
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && document.getElementById('modal-overlay')) {
    void closeModalSafe();
  }
});

// ── Garde générique "modifications non enregistrées" ───────────────────────
// Une page plein écran (édition projet/programme/action, notes de réunion…)
// enregistre un prédicat indiquant si elle a des modifications non sauvegardées.
// beforeunload (fermeture/refresh navigateur) et la navigation in-app (clic
// sidebar, géré dans boot.ts) le consultent pour avertir avant de perdre la
// saisie. La page d'édition de révision de projet garde son mécanisme propre
// (_confirmLeave) ; ce garde générique couvre les AUTRES pages plein écran.
// Une seule page "sale" à la fois — setUnsavedGuard remplace le garde précédent.
let _unsavedGuardFn: (() => boolean) | null = null;
let _unsavedBeforeUnload: ((e: BeforeUnloadEvent) => void) | null = null;

function setUnsavedGuard(fn: () => boolean): void {
  clearUnsavedGuard();
  _unsavedGuardFn = fn;
  _unsavedBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (_unsavedGuardFn && _unsavedGuardFn()) {
      e.preventDefault();
      e.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', _unsavedBeforeUnload);
  // Auto-nettoyage au prochain changement de page (le routeur invoque les
  // disposers à chaque dispatch).
  registerPageDisposer(clearUnsavedGuard);
}

function clearUnsavedGuard(): void {
  if (_unsavedBeforeUnload) {
    window.removeEventListener('beforeunload', _unsavedBeforeUnload);
    _unsavedBeforeUnload = null;
  }
  _unsavedGuardFn = null;
}

function hasUnsavedGuard(): boolean {
  return !!(_unsavedGuardFn && _unsavedGuardFn());
}

// Confirme la sortie si la page courante a des modifications non enregistrées.
// Renvoie true s'il est sûr de quitter (rien à sauver, ou l'utilisateur
// confirme). À appeler avant toute navigation in-app déclenchée par la page.
async function confirmLeaveIfDirty(): Promise<boolean> {
  if (!hasUnsavedGuard()) return true;
  const ok = await confirmStackModal({
    message: t('common.unsaved_leave_confirm'),
    danger: true,
    confirmLabel: t('common.unsaved_leave_btn'),
  });
  if (ok) clearUnsavedGuard();
  return ok;
}

// ── Router ────────────────────────────────────────────────────────────────
// Le moteur runtime vit dans `router-runtime.ts` (tranche extraite août 2026) ;
// il est réexposé sur `window` par le shim en bas de ce fichier.

function setContent(html: string): void {
  const el = document.getElementById('content');
  if (!el) return;
  // eslint-disable-next-line no-restricted-syntax -- helper central : html pré-échappé via esc() côté appelant (convention CLAUDE.md / DT1).
  el.innerHTML = html;
  _normalizeIconButtonAria();
}

function setHeader(titleHtml: string, actionsHtml = ''): void {
  const title = document.getElementById('page-title');
  const actions = document.getElementById('page-actions');
  // eslint-disable-next-line no-restricted-syntax -- helper central : titleHtml pré-échappé côté appelant.
  if (title) title.innerHTML = titleHtml;
  if (actions) {
    // eslint-disable-next-line no-restricted-syntax -- helper central : actionsHtml pré-échappé côté appelant.
    actions.innerHTML = actionsHtml;
    armHeaderButtons(actions); // inertes jusqu'à leur câblage (utils/header-buttons.ts)
  }
  _normalizeIconButtonAria();
}

// a11y : sur mobile, l'attribut `title` n'est pas lu par les lecteurs
// d'écran (et n'affiche pas de tooltip natif). Pour les boutons icône
// sans texte visible, on copie systématiquement `title` -> `aria-label`
// après chaque rendu de page ou de header. Hooké aussi dans openModal/
// openWideModal.
function _normalizeIconButtonAria(root: Document | HTMLElement = document): void {
  root.querySelectorAll<HTMLButtonElement>('button[title]:not([aria-label])').forEach((btn) => {
    const ttl = btn.getAttribute('title');
    if (!ttl) return;
    // On ne pose un aria-label QUE pour les boutons icône-seule : ceux dont
    // le contenu visible est vide ou un simple glyphe (✎, ✕, ◷, ▶, ⠿…).
    // Pour un bouton avec libellé texte, le nom accessible vient déjà du
    // texte — y superposer un aria-label créerait une divergence
    // visible/annoncé. Heuristique : aucun caractère "mot" (lettre/chiffre).
    const txt = (btn.textContent || '').trim();
    if (txt === '' || !/[\p{L}\p{N}]/u.test(txt)) {
      btn.setAttribute('aria-label', ttl);
    }
  });
}

// ── Shared loaders ────────────────────────────────────────────────────────

async function loadUsers(force = false): Promise<UserLite[]> {
  if (!force && _cache.users) return _cache.users;
  _cache.users = (await api.getUsers()) as UserLite[];
  return _cache.users;
}

/** Vrai si l'utilisateur peut être proposé dans un picker (RACI, etc.).
 *  Faux si `is_active === false` OU si la date du jour est > `departure_date`.
 *  Les équipes (`is_team`) sont concernées par les mêmes règles. */
function isUserActive(u: UserLite): boolean {
  if (u.is_active === false) return false;
  if (u.departure_date) {
    // Comparaison lexicographique sur ISO 'YYYY-MM-DD' = comparaison de dates.
    if (u.departure_date < todayStr()) return false;
  }
  return true;
}

/** Version filtrée de loadUsers : exclut les inactifs et les départs passés.
 *  À utiliser dans TOUS les pickers (RACI, approbateur, pilotes, comités…).
 *  `loadUsers()` (scope `select` côté back) exclut déjà les comptes admin
 *  techniques (DMO/W2U) ; les pages de gestion users/groupes passent par
 *  `api.getUsers({ manage: true })` pour les inclure. */
async function loadActiveUsers(force = false): Promise<UserLite[]> {
  const all = await loadUsers(force);
  return all.filter(isUserActive);
}

async function loadStages(force = false): Promise<StageLite[]> {
  if (!force && _cache.stages) return _cache.stages;
  _cache.stages = (await api.getStages()) as StageLite[];
  return _cache.stages;
}

async function loadRiskLists(force = false): Promise<RiskListLite[]> {
  if (!force && _cache.riskLists) return _cache.riskLists;
  _cache.riskLists = (await api.getRiskLists()) as RiskListLite[];
  return _cache.riskLists;
}

async function loadActionStatuses(force = false): Promise<ActionStatusLite[]> {
  if (!force && _cache.actionStatuses) return _cache.actionStatuses;
  _cache.actionStatuses = (await api.getActionStatuses()) as ActionStatusLite[];
  return _cache.actionStatuses;
}

// loadCommittees / invalidateCommittees : centralisation du cache (P1 #7).
// Avant : `window._cachedCommittees` posé/lu dans 3 modules
// (page-admin-committees, page-actions, component-action-modal) sans
// source de vérité claire — un edit côté admin ne propageait pas
// immédiatement à la liste actions ouverte. Convention `_cache.X` du
// helper pattern de core.ts, et `invalidateCommittees()` à appeler
// après tout CRUD réussi côté admin pour forcer le re-fetch.
async function loadCommittees(force = false): Promise<CommitteeLite[]> {
  if (!force && _cache.committees) return _cache.committees;
  _cache.committees = (await api.getCommittees()) as CommitteeLite[];
  // window._cachedCommittees conservé en alias rétrocompat — sera
  // retiré quand tous les call-sites passent par loadCommittees().
  window._cachedCommittees = _cache.committees;
  return _cache.committees;
}

function invalidateCommittees(): void {
  _cache.committees = null;
  delete window._cachedCommittees;
}

// setCommitteesCache : utilisé par la page admin après une mutation
// CRUD locale (post-create / post-update / post-delete / post-reorder)
// pour synchroniser le cache sans refetch HTTP. Évite que les autres
// pages (page-actions, component-action-modal) voient un état périmé.
function setCommitteesCache(committees: CommitteeLite[]): void {
  _cache.committees = committees;
  window._cachedCommittees = committees;
}

// ── Helpers Chart.js ──────────────────────────────────────────────────────
// Thème commun pour tous les graphes (donuts + histogrammes). On essaye
// de rester cohérent avec les variables CSS du projet (--accent, --bg, etc.).
//
// Palette de 12 couleurs réutilisables, choisies pour bien se distinguer
// même en daltonisme partiel. Si plus de 12 catégories, Chart.js boucle.
const CHART_PALETTE = [
  '#4F46E5',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#06B6D4',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#6366F1',
  '#84CC16',
  '#A855F7',
];

function chartColors(n: number): string[] {
  // Renvoie un tableau de n couleurs en bouclant sur la palette.
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(CHART_PALETTE[i % CHART_PALETTE.length] as string);
  return out;
}

function chartCommonOptions(): Record<string, unknown> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { font: { size: 11 } } },
      tooltip: { backgroundColor: 'rgba(0,0,0,0.85)', padding: 8 },
    },
  };
}

// Crée un canvas dimensionné dans `containerEl` et y instancie un Chart.
// Retourne l'instance Chart pour que le caller puisse `.destroy()` au
// changement de page. Si Chart n'est pas chargé, log un warning et
// retourne null (le smoke test attrapera l'erreur via console).
function makeChart(containerEl: HTMLElement, config: unknown): { destroy(): void } | null {
  if (typeof Chart === 'undefined') {
    safeWarn('makeChart', new Error('Chart.js non chargé'));
    containerEl.innerHTML = `<div style="color:var(--danger);padding:8px">${esc(t('common.chartMissing'))}</div>`;
    return null;
  }
  containerEl.innerHTML = '';
  const canvas = document.createElement('canvas');
  containerEl.appendChild(canvas);
  return new Chart(canvas, config);
}

// ── Picto « consomme un crédit IA » ─────────────────────────────────────────
// Badge orange (étincelle) à apposer sur tout CTA qui déclenche une opération
// d'IA facturée en crédit (analyse / génération de CR). But : prévenir
// l'utilisateur AVANT le clic qu'il va consommer un crédit. À réutiliser sur
// tout nouveau CTA d'IA facturée. Renvoie une chaîne HTML déjà échappée.
function iaCreditBadge(): string {
  return `<span class="ia-credit-badge" role="img" title="${esc(t('ia.creditBadge.tooltip'))}" aria-label="${esc(t('ia.creditBadge.aria'))}"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11 2.2l1.7 5.1 5.1 1.7-5.1 1.7L11 15.8 9.3 10.7 4.2 9l5.1-1.7z"/><path d="M17.8 13.6l.8 2.4 2.4.8-2.4.8-.8 2.4-.8-2.4-2.4-.8 2.4-.8z"/></svg></span>`;
}

// ── ESM globals shim (lot B.8) ──
// Expose les déclarations top-level sur window pour que les autres
// modules ESM puissent y accéder comme identifiants nus (bare refs).
// Avant la migration ESM, ces déclarations étaient des globals via
// <script src="..."> non-module. Maintenant elles vivent en scope
// module et il faut les ré-exposer pour préserver le comportement.
Object.assign(window, {
  _cache,
  _currentUserId,
  currentUserId,
  ensureCurrentUserId,
  todayStr,
  esc,
  safeWarn,
  safeToast,
  tlsSet,
  tlsGet,
  tlsRemove,
  I18N_DEFAULT_LOCALE,
  I18N_INTERP_RE,
  loadI18n,
  applyI18nDom,
  t,
  FALLBACK_SEV_COLOR,
  loadRiskColors,
  sevColor,
  sevLabel,
  scaleBtnHtml,
  scaleHtml,
  getDefaultStatusId,
  commitNewActions,
  fmtDate,
  fmtDateLocal,
  fmtDateTimeLocal,
  priorityLabel,
  riskCodeOrFallback,
  userLabel,
  individualUserOptions,
  toast,
  _modalPrevFocus,
  openModal,
  openWideModal,
  closeModal,
  openConfirmDangerModal,
  confirmStackModal,
  openFormModal,
  closeModalSafe,
  _modalHasUnsavedChanges,
  _snapshotModalFields,
  router,
  registerPageDisposer,
  _installFocusTrap,
  setUnsavedGuard,
  clearUnsavedGuard,
  hasUnsavedGuard,
  confirmLeaveIfDirty,
  updateNav,
  setContent,
  setHeader,
  _normalizeIconButtonAria,
  loadUsers,
  loadActiveUsers,
  isUserActive,
  loadStages,
  loadRiskLists,
  loadActionStatuses,
  loadCommittees,
  invalidateCommittees,
  setCommitteesCache,
  CHART_PALETTE,
  chartColors,
  chartCommonOptions,
  makeChart,
  iaCreditBadge,
});

// Marker module ESM (le runtime utilise window.X via le shim ci-dessus,
// pas d'export public à ce stade — découplage en S4-S5). Seuls les TYPES de
// handlers de page sont exportés, pour `router-runtime.ts` (import de type
// uniquement : effacé à la compilation, donc pas de cycle au runtime).
export type { PageHandler, PagesGlobal };
export {};
